import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpIcon } from "lucide-react";
import type { KeyboardEvent, RefObject } from "react";

interface ChatInputProps {
	value: string;
	onChange: (value: string) => void;
	onSubmit: () => void;
	disabled: boolean;
	placeholder: string;
	inputRef?: RefObject<HTMLInputElement | null>;
}

const ChatInput = ({
	value,
	onChange,
	onSubmit,
	disabled,
	placeholder,
	inputRef,
}: ChatInputProps) => {
	const reduceMotion = useReducedMotion();

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			onSubmit();
		}
	};

	return (
		<div className="shrink-0 border-slate-100 border-t p-3 dark:border-slate-800">
			<div className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1.5 pr-1.5 pl-4 transition-colors focus-within:border-sky-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-800 dark:focus-within:bg-slate-800 dark:focus-within:ring-sky-900/40">
				<input
					ref={inputRef}
					type="text"
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder={placeholder}
					className="min-w-0 flex-1 rounded-none border-0 bg-transparent p-0! text-sm shadow-none ring-0 placeholder:text-slate-400"
					aria-label="Chat with AI"
				/>
				<motion.button
					type="button"
					onClick={onSubmit}
					disabled={disabled}
					aria-label="Send"
					whileTap={reduceMotion ? undefined : { scale: 0.9 }}
					className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-sm transition-opacity disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none"
				>
					<ArrowUpIcon className="size-4" />
				</motion.button>
			</div>
		</div>
	);
};

export default ChatInput;
